import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { AttributeSchemaTypeEnum, IAttribute, IAttributeSchema } from "../../services/attribute-data-service/Models";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useGroup } from "../../queries/groups/UseGroup";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useAttributeSchemas } from "../../queries/attributes/UseAttributeSchemas";
import FormSelect from "../forms/form-select/FormSelect";
import { MenuItem, Typography } from "@mui/material";
import FormDatePicker from "../forms/form-date-picker/FormDatePicker";
import FormComboBox from "../forms/form-combo-box/FormComboBox";
import FormInput from "../forms/form-input/FormInput";
import FormProperty from "../forms/form-property/FormProperty";
import FormPropertySection from "../forms/form-property-section/FormPropertySection";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import AttributeSchemaEditorDialog, { bindAttributeSchemaEditorDialog, useAttributeSchemaEditorDialogState } from "../attribute-schema-editor/AttributeSchemaEditorDialog";
import { IeLynxObject } from "../../common/models/eLynxObject";

export type ApplyAttributeDialogInitValues = {
    targetObject?: IeLynxObject,
    currentAttributes: IAttribute[]
};

export type ApplyAttributeDialogResultValues = {
    attribute: IAttribute;
};

export type ApplyAttributeDialogResult = DialogResult<ApplyAttributeDialogResultValues>;

export type ApplyAttributeDialogProps = DialogProps<ApplyAttributeDialogInitValues, ApplyAttributeDialogResult>;

export type ApplyAttributeFormValuesType = {
    //aid: number;
    // Object Display Text
    //odt: string;
    // Object Type
    //ot: number;
    //// Object ID
    //oid: number;
    // Attribute Name
    an: string;
    // Attribute Value
    av: string;

    // Attribute External flag
    //ext: boolean;
    // Attribute Schema ID
    asid: number;

    // Attribute Schema
    //attrs?: IAttributeSchema;

    avdate?: Date | null;
    avnum?: number | null;

};

const defaultFormValue: ApplyAttributeFormValuesType = {
    //aid: -1;

    // Object Display Text
    //odt: string;
    // Object Type
    //ot: number;
    // Object ID
    //oid: number;
    // Attribute Name
    an: '',
    // Attribute Value
    av: '',

    // Attribute External flag
    //ext: boolean;
    // Attribute Schema ID
    asid: -1,
    // Attribute Schema
    //attrs?: IAttributeSchema;

    avdate: null,
    avnum: null

}

export default function ApplyAttributeDialog({
    open,
    onClose,
    values
}: ApplyAttributeDialogProps) {

    const [userState] = useUserContext();
    const { t } = useTranslation();
    const { confirm  } = useConfirmation();
    const routeContext = useRouteContext();
    const attributeSchemaEditorDialog = useAttributeSchemaEditorDialogState();
    

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    const {data: schemas, refetch } = useQueryNotifications(useAttributeSchemas)({
        groupID: routeContext.groupID as number,
        organizationID: group?.organizationID ?? -1,
        enabled: group !== undefined
    });

    const form = useForm<ApplyAttributeFormValuesType>({
        mode: "onChange",
        defaultValues: {...defaultFormValue} as ApplyAttributeFormValuesType
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid,
        },
        watch,
        setValue,
        reset
    } = form;

    const [
        asid,
        //an,
        av,
        avdate,
        avnum
    ] = watch([
        'asid',
        //'an',
        'av',
        'avdate',
        'avnum']);

    const availableSchemas = schemas?.filter(o => (values?.currentAttributes ?? []).find(v => v.asid === o.id) === undefined);
    const schema = schemas?.find(o => o.id === asid);
    const attributeType = schema?.schemaType ?? AttributeSchemaTypeEnum.None;

    const valid = isValid && asid !== -1 && (
        (attributeType === AttributeSchemaTypeEnum.Date && avdate !== null) ||
        (attributeType === AttributeSchemaTypeEnum.Numeric && avnum !== null) ||
        (attributeType !== AttributeSchemaTypeEnum.Date && av.trim().length > 0)
    );

    async function onSubmit(data: ApplyAttributeFormValuesType) {

        const ids = values?.currentAttributes.filter(o => o.aid < 0).map(o => o.aid) ?? [];
        let nextID = ids.length === 0 ? -1 : Math.min(...ids) - 1;
        if (nextID > 0) {
            nextID = -1;
        }
        const schema = schemas?.find(o => o.id === data.asid);

        const attr: IAttribute = {
        aid: nextID,

        // Object Display Text
        odt: '',
        // Object Type
        ot: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        // Object ID
        oid: routeContext.contextObject?.id as number,
        // Attribute Name
        an: schema?.name ?? data.an,

        // Attribute Value
        av: schema?.schemaType === AttributeSchemaTypeEnum.Date && data.avdate ?
                             data.avdate.toLocaleDateString() : 
            schema?.schemaType === AttributeSchemaTypeEnum.Numeric && 
            data.avnum !== null && data.avnum !== undefined ? 
            //Bug 53841: Numeric Attribute Bug - DOC
            //data.avnum.toLocaleString() : 
            data.avnum.toString() : 
            data.av,
        avdate: data.avdate,
        // Attribute External flag
        ext: true,
        // Attribute Schema ID
        asid: data.asid,
        // Attribute Schema
        attrs: {
            id: data.asid,
            cuid: schema?.createdBy ?? -1,
            dd: schema?.decimalDigits,
            name: schema?.name,
            stype: schema?.schemaType,
            vopts: schema?.valueOptions?.map((o,ndx) => {
                return {
                    id: o.id,
                    asid: o.schemaID,
                    val: o.value,
                    ord: ndx,
                }
            })

        } as IAttributeSchema

        }

        const returnData: ApplyAttributeDialogResultValues = {
            attribute: attr
        };

        reset();

        onClose && onClose({
            reason: "ok",
            values: returnData
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserExitConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function onSchemaChanged(value: number) {


        const schema = availableSchemas?.find(o => o.id === value);

        const name = schema?.name ?? '';

        setValue('an', name, {shouldDirty: true});
        setValue('av', '', {shouldDirty: true});
        setValue('avdate', null, {shouldDirty: true});
        setValue('avnum', null, {shouldDirty: true});

    }

    const _validateMaxWholeDigits = (value: string, wholeDigits: number | undefined): boolean => {

        if (wholeDigits === undefined) {
            return true;
        }

        const str = value.toString().trim().replace('-', '');
        const ndx = str.indexOf('.');
        return ndx !== -1 ? 
                str.substring(0, ndx).length  <= wholeDigits :  
                str.length  <= wholeDigits;
    }

    async function onCreateSchema() {

        const result = await attributeSchemaEditorDialog.open({
            organizationID: group?.organizationID as number,
            groupID: routeContext.groupID as number,
            targetObject: routeContext.contextObject,
            attributeSchemaID: -1,
            attributeSchemaName: ''
        });

        if (result.reason === 'save') {

            const asid = result.values?.attributeSchemaID as number;
            await refetch();
            setValue('asid', asid, {shouldDirty: true})
        }
    }

    useEffect(() => {

        reset({...defaultFormValue});
    }, [
        reset,
        values
    ]);

    return (
        <>
            <ResponsiveDialog
                title={t("actions.applyAttribute")}
                enableOkay={isDirty && valid}
                okayLabel={t("buttons.apply")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onSubmit(data))()
                            : onCancel();
                    }
                }
                enableAdditionalAction={true}
                additionalActionLabel={t("attributeSchema.createNew")}
                onAdditionalAction={() => onCreateSchema()}

            >
                <Form form={form}>

                    <FormPropertySection label='' sx={{marginBottom: 2}}>
                        <Typography variant='body1'>
                            {t('attributeSchema.applyInfoText', {objectType: getObjectTypeName(routeContext.contextObject?.objectType)})}
                        </Typography>
                    </FormPropertySection>


                    <FormProperty>

                        <FormSelect
                                name='asid'
                                label={t('properties.attributeName')}
                                sx={{ width: "100%" }}
                                onChange={(e) => onSchemaChanged(+e.target.value)}
                            > 
                            <MenuItem value={-1} disabled={true}>
                                    
                            </MenuItem>

                            {availableSchemas?.map((o, ndx) =>
                                <MenuItem key={ndx} value={o.id}>
                                    {o.name}
                                </MenuItem>
                            )}
                        </FormSelect> 
                    </FormProperty>

                    <FormProperty>


                    {
                attributeType === AttributeSchemaTypeEnum.Date ?
                    <FormDatePicker
                        name='avdate'
                        label={t('properties.attributeValue')}
                        views={['month','day', 'year']}
                    />
                :
                attributeType === AttributeSchemaTypeEnum.PredefinedValue ?

                    <FormSelect
                        name='av'
                        label={t('properties.attributeValue')}
                        sx={{ width: "100%" }}
                    > 
                    {schema?.valueOptions?.map((o, ndx) =>
                        <MenuItem key={ndx} value={o.value}>
                            {o.value}
                        </MenuItem>
                
                    )}
                    </FormSelect> 
                :
                attributeType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ?
                <FormComboBox 
                    name={'av'}
                    label={t('properties.attributeValue')}
                    sx={{ width: "100%" }}                        
                    options={schema?.valueOptions?.map(o => o.value) ?? []}
                    rules={{
                        required: true,
                        maxLength: 128,
                        message: t('properties.maxAttributeLength')
                    }}
                    // onChange={(newvalue) => {
                    //     const foo = newvalue;
                    // }}
                />
                :
                attributeType === AttributeSchemaTypeEnum.Numeric ?
                <FormInput
                    name='avnum'
                    label={t('properties.attributeValue')}
                    rules={{
                        required: true,
                        validate: attributeType !== AttributeSchemaTypeEnum.Numeric ? undefined : 
                                    (value: string) => !_validateMaxWholeDigits(value, schema?.wholeDigits) ?
                            t('attributeSchema.maxWholeDigits', {value: schema?.wholeDigits}) : 
                            true
                    }}
                    sx={{ width: "100%" }}
                    decimals={schema?.decimalDigits}
                    numeric={true}
                />
                :
                <FormInput
                    name='av'
                    label={t('properties.attributeValue')}
                    disabled={attributeType === AttributeSchemaTypeEnum.None}
                    rules={{
                        required: true,
                        maxLength: 128,
                        message: t('properties.maxAttributeLength'),
                    }}
                    sx={{ width: "100%" }}
                />

                }

                    </FormProperty>

                </Form>

            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />

            <AttributeSchemaEditorDialog {...bindAttributeSchemaEditorDialog(attributeSchemaEditorDialog)} />

        </>
    );
}

export type ApplyAttributeDialogState = DialogState<ApplyAttributeDialogInitValues, ApplyAttributeDialogResult>;
export type BindApplyAttributeDialogReturn = BindDialogReturn<ApplyAttributeDialogInitValues, ApplyAttributeDialogResult>;

export function useApplyAttributeDialogState(): ApplyAttributeDialogState {
    return useDialogState();
}

export function bindApplyAttributeDialog(props: ApplyAttributeDialogState): BindApplyAttributeDialogReturn {
    return bindDialog(props);
}
